import type { MapDefinition, Point } from './world-types.js';

/** Vision uses a 0.75-metre tile, measured from the hero's centre. */
export const FOG_TILE_SIZE = 0.75;
export const HERO_VISION_TILES = 32;
export const HERO_VISION_RADIUS = FOG_TILE_SIZE * HERO_VISION_TILES;

export interface FogState {
  revision: number;
  tileSize: number;
  columns: number;
  rows: number;
  visionRadius: number;
  /** Each hero that sees, with the cave it walks (`UnitState.level`) where it has one. */
  sources: (Point & { level?: number })[];
  /** Sorted row-major [start, exclusive end] pairs of explored tile indices. */
  explored: number[];
  /** Development hosts only: the party sees the entire map, so no veil is drawn. */
  revealed?: boolean;
}

export function fogTileIndex(map: MapDefinition, fog: Pick<FogState, 'tileSize' | 'columns'>, point: Point): number | null {
  if (point.x < -map.width / 2 || point.x >= map.width / 2 || point.z < -map.depth / 2 || point.z >= map.depth / 2) return null;
  return Math.floor((point.z + map.depth / 2) / fog.tileSize) * fog.columns + Math.floor((point.x + map.width / 2) / fog.tileSize);
}

export function inPartyVision(fog: Pick<FogState, 'sources' | 'visionRadius'>, point: Point): boolean {
  return fog.sources.some(source => (point.x - source.x) ** 2 + (point.z - source.z) ** 2 <= fog.visionRadius ** 2);
}

export function isExplored(map: MapDefinition, fog: FogState, point: Point): boolean {
  const index = fogTileIndex(map, fog, point);
  if (index === null) return false;
  let low = 0, high = fog.explored.length / 2;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (fog.explored[middle * 2 + 1] <= index) low = middle + 1;
    else high = middle;
  }
  return low < fog.explored.length / 2 && fog.explored[low * 2] <= index;
}
